/**
 * AI CONTEXT: sales-psychology - Recent sales popup system with frequency control
 * Journal: J-20250117-01
 * 
 * This component implements a social proof popup system that:
 * - Displays recent customer purchases to encourage new sales
 * - Shows customer names, locations, and purchase details
 * - Automatically rotates through different recent purchases
 * - Positions in bottom-left corner for maximum visibility
 * - Includes call-to-action button to store
 * - Supports configurable frequency: constant, reduced, or minimal
 * 
 * Future enhancements:
 * - Add purchase amount display
 * - Implement location-based targeting
 * - Add purchase timing (e.g., "2 minutes ago")
 * - Integrate with analytics for conversion tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingBag, X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RecentSalesPopup = ({ frequency = 'constant' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSale, setCurrentSale] = useState(null);
  const [sales, setSales] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [isLoading, setIsLoading] = useState(false); // Used for future loading states
  const navigate = useNavigate();

  // Frequency control configuration
  const frequencyConfig = {
    constant: {
      initialDelay: 3000,      // 3 seconds
      rotationInterval: 8000,   // 8 seconds
      reShowDelay: 30000,      // 30 seconds after close
      showProbability: 1.0     // Always show
    },
    reduced: {
      initialDelay: 15000,     // 15 seconds
      rotationInterval: 30000,  // 30 seconds
      reShowDelay: 120000,     // 2 minutes after close
      showProbability: 0.7     // 70% chance to show
    },
    minimal: {
      initialDelay: 60000,     // 1 minute
      rotationInterval: 120000, // 2 minutes
      reShowDelay: 300000,     // 5 minutes after close
      showProbability: 0.4     // 40% chance to show
    }
  };

  const config = frequencyConfig[frequency] || frequencyConfig.constant;

  // Fetch recent sales from API
  const fetchRecentSales = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // In development mode, use sample data immediately
      if (process.env.NODE_ENV === 'development') {
        console.log('Development mode: Using Utah-based sample data for recent sales popup');
        setSales([
          {
            id: '1',
            customerName: 'Jeffery M.',
            location: 'Utah',
            items: 2,
            productName: 'Tactical Gear'
          },
          {
            id: '2',
            customerName: 'James D.',
            location: 'St. George',
            items: 1,
            productName: 'Survival Kit'
          },
          {
            id: '3',
            customerName: 'Jake B.',
            location: 'Utah',
            items: 3,
            productName: 'Training Equipment'
          },
          {
            id: '4',
            customerName: 'Gregory G.',
            location: 'St. George',
            items: 2,
              productName: 'Self Defense Tools'
          }
        ]);
        return;
      }
      
      // In production, try to fetch from API
      const apiUrl = process.env.REACT_APP_API_BASE_URL;
      if (apiUrl) {
        try {
          console.log('Production mode: Attempting to fetch from API:', `${apiUrl}/recent-sales`);
          const response = await fetch(`${apiUrl}/recent-sales`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            mode: 'cors'
          });
          if (response.ok) {
            const data = await response.json();
            setSales(data.sales || []);
            return;
          } else {
            console.warn('API returned non-OK status:', response.status, response.statusText);
          }
        } catch (apiError) {
          console.warn('API call failed:', apiError);
        }
      }
      
      // Fallback to sample data if API fails
      throw new Error('No API available');
      
    } catch (error) {
      console.warn('Failed to fetch recent sales:', error);
      // Fallback to Utah-based sample data
      setSales([
        {
          id: '1',
          customerName: 'Jeffery M.',
          location: 'Utah',
          items: 2,
          productName: 'Tactical Gear'
        },
        {
          id: '2',
          customerName: 'James D.',
          location: 'St. George',
          items: 1,
          productName: 'Survival Kit'
        },
        {
          id: '3',
          customerName: 'Jake B.',
          location: 'Utah',
          items: 3,
          productName: 'Training Equipment'
        },
        {
          id: '4',
          customerName: 'Gregory G.',
          location: 'St. George',
          items: 2,
          productName: 'Self Defense Tools'
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Show popup with frequency-controlled delay and rotation
  useEffect(() => {
    if (sales.length === 0) return;

    // Check if we should show based on probability
    const shouldShow = Math.random() <= config.showProbability;
    if (!shouldShow) {
      console.log(`Popup frequency: ${frequency} - Skipping display (${(config.showProbability * 100).toFixed(0)}% chance)`);
      return;
    }

    // Initial delay before showing first popup
    const initialTimer = setTimeout(() => {
      setIsVisible(true);
      console.log(`Popup frequency: ${frequency} - Initial popup displayed after ${config.initialDelay / 1000}s`);
    }, config.initialDelay);

    // Rotate through sales at configured interval
    const rotationTimer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % sales.length);
    }, config.rotationInterval);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(rotationTimer);
    };
  }, [sales.length, frequency, config]);

  // Update current sale when index changes
  useEffect(() => {
    if (sales.length > 0) {
      setCurrentSale(sales[currentIndex]);
    }
  }, [currentIndex, sales]);

  // Fetch sales on component mount
  useEffect(() => {
    fetchRecentSales();
  }, [fetchRecentSales]);

  // Handle popup close
  const handleClose = () => {
    setIsVisible(false);
    // Re-show after configured delay
    setTimeout(() => {
      const shouldReShow = Math.random() <= config.showProbability;
      if (shouldReShow) {
        setIsVisible(true);
        console.log(`Popup frequency: ${frequency} - Re-showing after ${config.reShowDelay / 1000}s`);
      } else {
        console.log(`Popup frequency: ${frequency} - Skipping re-show (${(config.showProbability * 100).toFixed(0)}% chance)`);
      }
    }, config.reShowDelay);
  };

  // Handle store navigation
  const handleShopNow = () => {
    navigate('/store');
    setIsVisible(false);
  };

  // Don't render if no sales or not visible
  if (!isVisible || !currentSale || sales.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 z-50 animate-fade-in-up sm:bottom-6 sm:left-6 md:bottom-6 md:left-6">
      <div className="recent-sales-popup bg-theme-black/95 border-2 border-custom-orange/50 rounded-lg p-2 sm:p-3 max-w-xs sm:max-w-sm mx-2 sm:mx-0">
        {/* Header with close button */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <ShoppingBag className="h-5 w-5 text-custom-orange drop-shadow-sm" />
            <span className="text-sm font-semibold text-white text-shadow">
              Recent Purchase
            </span>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-300 hover:text-white transition-colors drop-shadow-sm"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Sale content */}
        <div className="mb-3">
          <p className="text-sm text-gray-100 leading-relaxed text-shadow">
            <span className="font-semibold text-white">
              {currentSale.customerName}
            </span>{' '}
            from{' '}
            <span className="font-semibold text-white">
              {currentSale.location}
            </span>{' '}
            recently purchased{' '}
            <span className="font-semibold text-custom-orange drop-shadow-sm">
              {currentSale.items} {currentSale.items === 1 ? 'item' : 'items'}
            </span>{' '}
            to enter into our giveaways!
          </p>
        </div>

        {/* Call to action */}
        <button
          onClick={handleShopNow}
          className="w-full bg-custom-orange hover:bg-custom-orange/90 text-white font-bold py-3 px-4 rounded-md transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          <span className="text-shadow">Shop Now</span>
          <ArrowRight className="h-4 w-4 drop-shadow-sm" />
        </button>

        {/* Progress indicator */}
        <div className="mt-3 flex justify-center space-x-2">
          {sales.map((_, index) => (
            <div
              key={index}
              className={`h-2.5 w-2.5 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-custom-orange shadow-lg scale-110'
                  : 'bg-gray-500/60'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default RecentSalesPopup;
